/**
 * @file BillFeatureDropdown.js
 * @description A dropdown component for selecting a billable feature.
 */

import React, { useState, useEffect } from 'react';
import { fetchBillFeatures } from '../../../services/systemService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';

/**
 * A dropdown component for selecting a billable feature.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label should be displayed inline.
 * @param {number} props.selectedFeature - The ID of the selected bill feature.
 * @param {Function} props.onFeatureChange - The callback function to be called when the selection changes.
 * @param {string} [props.label="Feature"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @returns {JSX.Element} The rendered BillFeatureDropdown component.
 */
const BillFeatureDropdown = ({
    className,
    inlineLabel = true,
    selectedFeature,
    onFeatureChange,
    label = 'Feature',
    isRequired = false,
}) => {
    const [features, setFeatures] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getFeatures = async () => {
            setIsLoading(true);
            try {
                const data = await fetchBillFeatures();
                setFeatures(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Could not fetch bill features:', error);
                Toast.error('Unable to load service features.');
            } finally {
                setIsLoading(false);
            }
        };

        getFeatures();
    }, []);

    const getDefaultLabel = () => {
        const matchingFeature = features.find(
            feature => feature.id === selectedFeature,
        );
        return matchingFeature ? matchingFeature.name : 'Select Feature';
    };

    const featureOptions = features.map(feature => ({
        value: feature.id,
        label: feature.name,
    }));

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <label htmlFor="bill-feature-dropdown">
                {label}
                {isRequired && <span className="text-danger">*</span>}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    id="bill-feature-dropdown"
                    defaultValue={getDefaultLabel()}
                    options={featureOptions}
                    value={selectedFeature}
                    onChange={onFeatureChange}
                    disabled={features.length === 0 || isLoading}
                />
            </LoadingWrapper>
        </div>
    );
};

export default BillFeatureDropdown;
